/**
 * Birthday wheel picker — Flutter CommerceBirthdayWheelPicker parity.
 * Three CSS-scroll-snap columns (year/month/day) replacing the native
 * <input type="date"> UI, matching Flutter's inline iOS-style wheel
 * (mobile_app's CupertinoPicker triple-column layout) instead of a
 * calendar-grid/native OS date dialog. 2026-08-08.
 */
(function () {
  var ITEM_H = 36;

  function daysInMonth(year, month) {
    return new Date(year, month, 0).getDate();
  }

  function pad2(n) {
    return String(n).padStart(2, '0');
  }

  // The wheel is often built inside a detached subtree (a still-unmounted
  // form/step panel) before being attached to the document as one later
  // step in the caller's own flow — sometimes more than one frame away.
  // scrollTop assignment silently no-ops on an element with no layout box
  // yet, so wait until it's genuinely connected AND has a real height
  // rather than assuming one rAF is enough. Confirmed live 2026-08-08.
  function whenReady(el, fn) {
    if (el.isConnected && el.clientHeight > 0) {
      fn();
      return;
    }
    var tries = 0;
    var timer = window.setInterval(function () {
      tries += 1;
      if ((el.isConnected && el.clientHeight > 0) || tries >= 100) {
        window.clearInterval(timer);
        if (el.isConnected && el.clientHeight > 0) fn();
      }
    }, 50);
  }

  function monthLabels(locale) {
    var fmt;
    try {
      fmt = new Intl.DateTimeFormat(locale || undefined, { month: 'long' });
    } catch (e) {
      fmt = new Intl.DateTimeFormat(undefined, { month: 'long' });
    }
    var out = [];
    for (var m = 0; m < 12; m++) {
      out.push(fmt.format(new Date(2000, m, 1)));
    }
    return out;
  }

  function buildColumn(list, labelFn, initialIndex, onSelect, ariaLabel) {
    var col = document.createElement('div');
    col.className = 'eros-birthday-wheel__col';
    col.setAttribute('role', 'listbox');
    col.setAttribute('aria-label', ariaLabel);
    col.setAttribute('tabindex', '0');

    var topPad = document.createElement('div');
    topPad.className = 'eros-birthday-wheel__pad';
    col.appendChild(topPad);

    var items = list.map(function (value, i) {
      var el = document.createElement('div');
      el.className = 'eros-birthday-wheel__item';
      el.textContent = labelFn(value, i);
      el.setAttribute('role', 'option');
      col.appendChild(el);
      return el;
    });

    var botPad = document.createElement('div');
    botPad.className = 'eros-birthday-wheel__pad';
    col.appendChild(botPad);

    var selected = -1;
    function markSelected(index) {
      if (index === selected) return;
      if (items[selected]) items[selected].classList.remove('is-selected');
      selected = index;
      if (items[selected]) items[selected].classList.add('is-selected');
    }

    function scrollToIndex(index, smooth) {
      // 'instant' is not a universally recognized ScrollToOptions.behavior
      // value (only 'auto'/'smooth' are spec-guaranteed) — some engines
      // silently no-op the whole call rather than falling back, which left
      // every wheel stuck at scrollTop 0 regardless of the requested index.
      // Plain scrollTop assignment always applies synchronously. Confirmed
      // live 2026-08-08.
      if (smooth) {
        col.scrollTo({ top: index * ITEM_H, behavior: 'smooth' });
      } else {
        col.scrollTop = index * ITEM_H;
      }
      markSelected(index);
    }

    var settleTimer = 0;
    function onScroll() {
      markSelected(Math.round(col.scrollTop / ITEM_H));
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(function () {
        var idx = Math.max(0, Math.min(items.length - 1, Math.round(col.scrollTop / ITEM_H)));
        markSelected(idx);
        onSelect(idx);
      }, 120);
    }
    col.addEventListener('scroll', onScroll, { passive: true });

    col.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      e.preventDefault();
      var idx = Math.max(0, Math.min(items.length - 1, Math.round(col.scrollTop / ITEM_H) + (e.key === 'ArrowDown' ? 1 : -1)));
      scrollToIndex(idx, true);
      onSelect(idx);
    });

    items.forEach(function (el, i) {
      el.addEventListener('click', function () {
        scrollToIndex(i, true);
        onSelect(i);
      });
    });

    // Deferred to the caller (after this element is attached to the
    // document) — scrollTo() on a still-detached node is a silent no-op,
    // which left every wheel showing at scrollTop 0 (all "1900") instead
    // of the intended initial value. Confirmed live 2026-08-08.
    return { el: col, scrollToIndex: scrollToIndex, itemCount: function () { return items.length; } };
  }

  /**
   * mount(container, {value, onChange, firstYear, locked, locale, labels})
   * value: Date (or null → defaults to 18 years ago, Jan 15, matching Flutter)
   * onChange(dateStr): called with 'YYYY-MM-DD' whenever the resolved date changes
   */
  function mount(container, opts) {
    opts = opts || {};
    var firstYear = opts.firstYear || 1900;
    var lastYear = new Date().getFullYear();
    var initial = opts.value instanceof Date && !isNaN(opts.value) ? opts.value : (function () {
      var d = new Date();
      d.setFullYear(d.getFullYear() - 18, 0, 15);
      return d;
    })();

    var year = initial.getFullYear();
    var month = initial.getMonth() + 1;
    var day = Math.min(initial.getDate(), daysInMonth(year, month));

    container.classList.add('eros-birthday-wheel');
    if (opts.locked) container.classList.add('is-locked');

    var body = document.createElement('div');
    body.className = 'eros-birthday-wheel__body';
    var highlight = document.createElement('div');
    highlight.className = 'eros-birthday-wheel__highlight';
    highlight.setAttribute('aria-hidden', 'true');
    var cols = document.createElement('div');
    cols.className = 'eros-birthday-wheel__cols';
    body.appendChild(highlight);
    body.appendChild(cols);
    container.appendChild(body);

    function emit() {
      var d = pad2(day) + '';
      var value = year + '-' + pad2(month) + '-' + pad2(day);
      if (opts.onChange) opts.onChange(value);
    }

    var years = [];
    for (var y = firstYear; y <= lastYear; y++) years.push(y);
    var months = monthLabels(opts.locale);

    var dayCol;
    function rebuildDays() {
      var count = daysInMonth(year, month);
      if (day > count) day = count;
      var list = [];
      for (var d2 = 1; d2 <= count; d2++) list.push(d2);
      var newCol = buildColumn(list, function (v) { return pad2(v); }, day - 1, function (idx) {
        day = idx + 1;
        emit();
      }, opts.labels && opts.labels.day || 'Day');
      if (dayCol) cols.replaceChild(newCol.el, dayCol.el);
      else cols.appendChild(newCol.el);
      dayCol = newCol;
      var dayAtBuild = day;
      whenReady(newCol.el, function () {
        newCol.scrollToIndex(dayAtBuild - 1, false);
      });
    }

    var yearCol = buildColumn(years, function (v) { return String(v); }, year - firstYear, function (idx) {
      year = firstYear + idx;
      rebuildDays();
      emit();
    }, opts.labels && opts.labels.year || 'Year');

    var monthCol = buildColumn(months, function (_, i) { return months[i]; }, month - 1, function (idx) {
      month = idx + 1;
      rebuildDays();
      emit();
    }, opts.labels && opts.labels.month || 'Month');
    monthCol.el.classList.add('eros-birthday-wheel__col--month');

    cols.appendChild(yearCol.el);
    cols.appendChild(monthCol.el);
    rebuildDays();
    var initialYear = year;
    var initialMonth = month;
    whenReady(yearCol.el, function () {
      yearCol.scrollToIndex(initialYear - firstYear, false);
    });
    whenReady(monthCol.el, function () {
      monthCol.scrollToIndex(initialMonth - 1, false);
    });

    return {
      getValue: function () {
        return year + '-' + pad2(month) + '-' + pad2(day);
      },
      setLocked: function (locked) {
        container.classList.toggle('is-locked', !!locked);
      },
    };
  }

  window.ErosBirthdayWheel = { mount: mount };
})();
